import { Component, computed, DestroyRef, forwardRef, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { FloatLabelModule } from '@openng/optimus-ui/floatlabel';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { SelectModule } from '@openng/optimus-ui/select';
import { SelectButtonModule } from '@openng/optimus-ui/selectbutton';

import { CustomControlValueAccessor } from '@app/shared/base/control-value-accessor';
import {
    type QnAConfig,
    QnaFieldComponent,
} from '@app/shared/components/dynamic-form/qna-field.component';

export interface DynamicFormField {
    key: string; // form control name
    type: 'textbox' | 'numberbox' | 'selectbox' | 'selectbutton' | 'qna';
    label: string; // display label
    placeholder?: string;
    initialValue?: string | null;
    defaultValue?: string | null;
    // for selectbox - simple string array
    options?: string[];
    // for qna - optional configuration
    qnaConfig?: QnAConfig;
    // validators configuration
    required?: boolean;
}

@Component({
    selector: 'cor-dynamic-field',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DynamicFieldComponent),
            multi: true,
        },
    ],
    imports: [
        ReactiveFormsModule,
        InputTextModule,
        SelectModule,
        SelectButtonModule,
        FloatLabelModule,
        QnaFieldComponent,
    ],
    template: `
        @switch (field()?.type) {
            @case ('textbox') {
                <input
                    pInputText
                    [id]="field().key"
                    [placeholder]="field().placeholder ?? ''"
                    [formControl]="internalControl"
                    fluid
                    (blur)="onTouched()"
                />
            }
            @case ('numberbox') {
                <input
                    pInputText
                    type="number"
                    [id]="field().key"
                    [placeholder]="field().placeholder ?? ''"
                    [formControl]="internalControl"
                    fluid
                    (blur)="onTouched()"
                />
            }
            @case ('selectbutton') {
                <p-selectbutton
                    [options]="selectOptions() ?? []"
                    [formControl]="internalControl"
                    [id]="field().key"
                    [unselectable]="false"
                    optionLabel="label"
                    optionValue="value"
                    (onBlur)="onTouched()"
                />
            }
            @case ('selectbox') {
                <p-select
                    [options]="selectOptions() ?? []"
                    [filter]="true"
                    [formControl]="internalControl"
                    [inputId]="field().key"
                    [attr.test-id]="'dynamic-field-select-' + field().key"
                    [placeholder]="field().placeholder ?? 'Select an option'"
                    optionLabel="label"
                    optionValue="value"
                    [appendTo]="'body'"
                    fluid
                    (onBlur)="onTouched()"
                >
                    <ng-template pTemplate="item" let-option>
                        <span [attr.test-id]="'dynamic-field-option-' + option.label">
                            {{ option.label }}
                        </span>
                    </ng-template>
                </p-select>
            }
            @case ('qna') {
                <cor-qna-field [qnaConfig]="field().qnaConfig" [formControl]="internalControl" />
            }
        }
    `,
})
export class DynamicFieldComponent extends CustomControlValueAccessor<string | null> {
    /** Field configuration (with selectOptions when type is selectbox/selectbutton) */
    readonly field = input.required<DynamicFormField>();

    /** Internal control used to drive the template and sync with CVA */
    readonly internalControl = new FormControl<string | null>(null);

    private readonly destroyRef = inject(DestroyRef);
    protected readonly selectOptions = computed(
        () => this.field()?.options?.map(option => ({ label: option, value: option })) || [],
    );

    constructor() {
        super();
        this.internalControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(value => this.onChange?.(value?.toString() ?? null));
    }

    writeValue(value: string | null): void {
        this.internalControl.setValue(value, { emitEvent: false });
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.internalControl.disable({ emitEvent: false });
        } else {
            this.internalControl.enable({ emitEvent: false });
        }
    }
}
